import type { AdoptSettings } from "../../api/types";
import { Badge } from "../../components/Badge";
import { vendorName } from "../../i18n/oauthCopy";
import { en } from "../../i18n/en";
import { t, tDynamic } from "../../i18n/t";
import { AdoptSettingsNotice } from "../scope/AdoptSettingsNotice";

/** One preflight finding, as `stage_profile` stores it and `GET /profile` / the `failed` event carry it. */
export type PreflightFinding = {
  code: string;
  severity: string;
  message: string;
  source_value?: string | null;
  destination_value?: string | null;
};

/**
 * Every code the connectors and `validation/preflight.py` emit. `tests/preflightIssues.test.tsx` pins that each
 * has a title and a next step in `en.ts`; an unknown code (a new connector, say) falls back to its message.
 */
export const PREFLIGHT_CODES: readonly string[] = [
  "ADOPT_SOURCE_SETTINGS",
  "BASE_CURRENCY",
  "FY_END",
  "VAT_SCHEME",
  "VAT_FREQUENCY",
  "VAT_NUMBER",
  "VAT_UNVERIFIED",
  "VAT_REGISTRATION",
  "VAT_FLAT_RATE",
  "VAT_NUMBER_MISSING",
  "VAT_PERIOD",
  "NOT_EMPTY",
  "xero_not_empty",
  "SOURCE_ROLE",
  "SOURCE_ACTION_DENIED",
  "SOURCE_ACTIONS_UNKNOWN",
  "DEST_USER_ROLE",
  "PLAN_TIER",
  "SOURCE_PLAN_TIER",
  "DEST_LOCK_DATE",
  "SOURCE_LOCK_DATE",
  "SOURCE_NOT_LOCKED",
  "CONVERSION_AT_FY_END",
  "qbo_grant_expiring",
  "qbo_grant_too_short",
  "xero_locked_period",
  "xero_plan_tier_too_low",
  "xero_tracking_cap",
  "xero_tracking_option_cap",
  "xero_bank_lines_dropped",
  "xero_assets_draft_only_no_rollback",
  "xero_conversion_date_not_month_start",
  "xero_contact_number_duplicate",
  "xero_contact_name_collision",
  "xero_write_scopes_missing",
  "JOURNAL_TO_CONTROL",
  "CONTACT_NAME_SUFFIXED",
  "TAX_CODE_UNMAPPED",
  "OPENING_BALANCE_EQUITY_MISSING",
  "PLAN_TIER_TOO_LOW",
  "ATTACHMENT_OVERSIZE",
  "ATTACHMENT_TYPE",
  "freeagent_accountant_carve_outs",
  "freeagent_insufficient_level",
  "freeagent_page_count_mismatch",
  "sage50_audit_trail_does_not_tie",
  "sage50_fx_rate_underivable",
  "sage50_journal_grouping_ambiguous",
  "sage50_probe_field_unavailable",
  "sage50_tb_leg_unavailable",
];

/**
 * The BLOCKs an operator can answer from the status page, mapped to the scope override the API records
 * (`set_preflight_override`). Everything else has to be fixed in a vendor's own settings; in particular
 * SOURCE_NOT_LOCKED is deliberately absent: loosening a lock-date safety gate is the owner's call, so it is
 * answered by setting the lock date in the source.
 */
export type OverrideChoice = {
  id: string;
  /** A function (not a key) so each string is a literal translation-call the i18n meta-test can see. */
  label: () => string;
  body: { allow_non_empty: true } | { fy_end_policy: "roll" | "carry" } | { adopt_source_settings: true };
  /** Shown only under "Advanced" when the finding is a WARN (the default needs no answer; this is the exception). */
  advanced?: boolean;
};

export const OVERRIDES_BY_CODE: Readonly<Record<string, readonly OverrideChoice[]>> = {
  NOT_EMPTY: [
    { id: "non_empty", label: () => t("preflight.override_non_empty"), body: { allow_non_empty: true } },
  ],
  xero_not_empty: [
    { id: "non_empty", label: () => t("preflight.override_non_empty"), body: { allow_non_empty: true } },
  ],
  // xmg-orgadopt item 6: the year-end policy defaults to "roll", so the finding is a WARN that needs no answer;
  // "carry" is the exception and lives under Advanced. A stale BLOCK from before the default still offers both.
  CONVERSION_AT_FY_END: [
    { id: "fy_roll", label: () => t("preflight.override_fy_roll"), body: { fy_end_policy: "roll" } },
    {
      id: "fy_carry",
      label: () => t("preflight.override_fy_carry"),
      body: { fy_end_policy: "carry" },
      advanced: true,
    },
  ],
  // xmg-orgadopt: consent to set an EMPTY LuciBook organisation's year end / VAT settings from the source.
  ADOPT_SOURCE_SETTINGS: [
    { id: "adopt", label: () => t("preflight.override_adopt"), body: { adopt_source_settings: true } },
  ],
};

function hasValue(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== "";
}

function known(code: string, part: "title" | "next"): boolean {
  return `preflight.code.${code}.${part}` in en;
}

/** BLOCKs first, then WARNs, each group in the order the engine produced them. */
export function orderFindings(findings: readonly PreflightFinding[]): PreflightFinding[] {
  return [
    ...findings.filter((f) => f.severity === "BLOCK"),
    ...findings.filter((f) => f.severity !== "BLOCK"),
  ];
}

export function PreflightIssues({
  findings,
  sourceSystem,
  destinationSystem,
  overridable,
  renderOverride,
  adopt,
}: {
  findings: readonly PreflightFinding[];
  sourceSystem: string | null;
  destinationSystem: string | null;
  /** Only a BLOCK on a failed-at-profiling migration may offer its override. */
  overridable: boolean;
  renderOverride: (choice: OverrideChoice) => React.ReactNode;
  /** xmg-orgadopt: the structured proposal behind an `ADOPT_SOURCE_SETTINGS` finding, when the API served one. */
  adopt?: AdoptSettings | null;
}): JSX.Element | null {
  const ordered = orderFindings(findings);
  if (ordered.length === 0) return null;
  const blocking = ordered.filter((f) => f.severity === "BLOCK");
  const warnings = ordered.filter((f) => f.severity !== "BLOCK");
  const sourceVendor = vendorName(sourceSystem);
  const destinationVendor = vendorName(destinationSystem);

  function renderIssue(issue: PreflightFinding, index: number): JSX.Element {
    const isBlock = issue.severity === "BLOCK";
    const offered = overridable ? (OVERRIDES_BY_CODE[issue.code] ?? []) : [];
    // A BLOCK offers every answer; a WARN only offers the ones that are a proposal to accept or an exception.
    const choices = isBlock
      ? offered
      : offered.filter((choice) => choice.advanced === true || choice.id === "adopt");
    const plain = choices.filter((choice) => choice.advanced !== true);
    const advancedChoices = isBlock ? [] : choices.filter((choice) => choice.advanced === true);
    const showAdopt = issue.code === "ADOPT_SOURCE_SETTINGS" && adopt !== null && adopt !== undefined;
    return (
      <li
        key={`${issue.code}-${index}`}
        className={`preflight-issue preflight-issue--${isBlock ? "block" : "warn"}`}
      >
        <p>
          <Badge tone={isBlock ? "fail" : "warn"}>{tDynamic("severity.", isBlock ? "BLOCK" : "WARN")}</Badge>{" "}
          <strong>
            {known(issue.code, "title") ? tDynamic("preflight.code.", `${issue.code}.title`) : issue.message}
          </strong>
        </p>
        {hasValue(issue.source_value) || hasValue(issue.destination_value) ? (
          <dl className="preflight-values">
            {hasValue(issue.source_value) ? (
              <>
                <dt>{t("preflight.says", { vendor: sourceVendor })}</dt>
                <dd>{issue.source_value}</dd>
              </>
            ) : null}
            {hasValue(issue.destination_value) ? (
              <>
                <dt>{t("preflight.has", { vendor: destinationVendor })}</dt>
                <dd>{issue.destination_value}</dd>
              </>
            ) : null}
          </dl>
        ) : null}
        {showAdopt ? (
          <AdoptSettingsNotice adopt={adopt} />
        ) : known(issue.code, "title") ? (
          <p>{issue.message}</p>
        ) : null}
        {known(issue.code, "next") ? (
          <p>
            <strong>{t("preflight.what_to_do")}:</strong>{" "}
            {tDynamic("preflight.code.", `${issue.code}.next`, { vendor: sourceVendor })}
          </p>
        ) : null}
        {isBlock ? (
          choices.length > 0 ? (
            <div className="row">{choices.map((choice) => renderOverride(choice))}</div>
          ) : null
        ) : (
          <>
            {plain.length > 0 ? (
              <div className="row">{plain.map((choice) => renderOverride(choice))}</div>
            ) : null}
            {advancedChoices.length > 0 ? (
              <details data-testid="preflight-advanced">
                <summary>{t("preflight.advanced")}</summary>
                <div className="row">{advancedChoices.map((choice) => renderOverride(choice))}</div>
              </details>
            ) : null}
          </>
        )}
        <p className="muted mono">{t("preflight.code", { code: issue.code })}</p>
      </li>
    );
  }

  return (
    <div data-testid="preflight-issues">
      {blocking.length > 0 ? (
        <section aria-label={t("preflight.blocking_heading", { count: blocking.length })}>
          <h4>{t("preflight.blocking_heading", { count: blocking.length })}</h4>
          <ul className="preflight-list">{blocking.map(renderIssue)}</ul>
        </section>
      ) : null}
      {warnings.length > 0 ? (
        <section aria-label={t("preflight.warning_heading", { count: warnings.length })}>
          <h4>{t("preflight.warning_heading", { count: warnings.length })}</h4>
          <ul className="preflight-list">{warnings.map(renderIssue)}</ul>
        </section>
      ) : null}
    </div>
  );
}
